import { action, computed, makeObservable, observable } from 'mobx';
import { UserActivityCategory, UserActivityTypes } from '@pascal/iframe-service';

import { GameCategory, LaunchType, GameRedirectionSource, SideMenuIcons, NotificationPriority, GameMode } from '../enums';
import { BigWinEvent, GameLaunchInfoRequest, LaunchData, TopWin, TopWinnersRequest, VirtualGamesRequest } from '../models';
import { gameController, HttpResponse, jackpotSocket, launcherSocket } from '../api';
import { CCEuro24GameId, ErrorPrefix, T, THREE_MINUTES_MS } from '../constants';
import { getRelaunchUrl, isOffline, setTranslationValues } from '../helpers';
import { integration } from '../services';
import { GameErrorInfo } from '../models';
import { VirtualGame } from '../models';
import { RootStore } from '.';
import { failLaunch, finishLaunch, markLaunchStep, setLaunchContext } from '../services/launch-telemetry';

const RELAUNCH_COOLDOWN_MS = 15_000;
const RELAUNCH_JITTER_MS = 2_000;
const RELAUNCH_KEY = 'pg.lastRelaunchAt';
// A navigation that has not replaced the page by then failed or hung. Not shorter than the cooldown, so the
// Refresh button it hands back always relaunches for real instead of being suppressed.
const RELAUNCH_STALL_MS = RELAUNCH_COOLDOWN_MS;
// How often a relaunch waiting for the network re-reads it, in case the `online` event never arrives.
const RELAUNCH_NETWORK_POLL_MS = 1_000;

export class GameStore {

  private rootStore: RootStore;

  public launchData = new HttpResponse<LaunchData | null>(null);

  public virtualGames = new HttpResponse<Array<VirtualGame>>([]);

  public topWins = new HttpResponse<Array<TopWin>>([]);

  public gameReplayUrl = new HttpResponse<string>('');

  /**
   * @description the win currently playing in the replay scene (null - replay scene is closed)
   */
  public replayWin: TopWin | null = null;

  /**
   * @description the win the "ready to play?" dialog is shown for (null - dialog is closed)
   */
  public replayDialogWin: TopWin | null = null;

  public isReplayFinished = false;

  // the game frame was muted by us for the replay scene, so closing it must unmute it again
  private replayMutedGameSound = false;

  // the most recent pushed big-win event, kept so clicking its notification can open the replay scene
  private lastBigWin: BigWinEvent | null = null;

  // big wins received while the flush window is open; only the highest-odd one is pushed when it closes
  private bigWinBuffer: Array<BigWinEvent> = [];

  private bigWinFlushTimer: NodeJS.Timer | null = null;

  public gamePaused = false;

  public gameErrorInfo: GameErrorInfo | null = null;

  public gameReady = false;

  /**
   * @description user enabled sounds or not
   */
  public soundEnabled = false;

  public stakeAmount = 0;

  public roundInProgress = false;

  /**
   * @description game canvas width by px (only exist on games where in web devices the canvas width is different from the window width, e.g. Fortune Master Goat, Fortune Don Tiger, ...)
   */
  public canvasWidth = 0;

  /**
   * @description a jittered relaunch is scheduled, in flight, waiting for the network, or was suppressed by
   * the cooldown; either way the player must not be left looking at a Reload button that does nothing.
   * Cleared again when the navigation does not replace the page
   */
  public relaunchPending = false;

  /**
   * @description the relaunch cooldown suppressed the only exit from a page that has no launch data to render
   * anything else from. Sticky: clearing it would put the player back on a blank page. Splash renders the general
   * ErrorPage on it; its Refresh button relaunches for real once relaunchPending clears
   */
  public relaunchBlocked = false;

  // the launch token already exchanged for launch data, so it is never redeemed twice
  private redeemedToken = '';

  // the jittered navigation, then the watchdog for a navigation that never replaced the page
  private relaunchTimer: NodeJS.Timeout | null = null;

  // set while a relaunch waits for the network to come back; calling it drops the listeners
  private stopWaitingForNetwork: (() => void) | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      virtualGames: observable,
      setVirtualGames: action.bound,

      gameErrorInfo: observable,
      setGameErrorInfo: action.bound,

      launchData: observable,
      setLaunchData: action.bound,

      topWins: observable,
      setTopWins: action.bound,

      gameReplayUrl: observable,
      setGameReplayUrl: action.bound,

      replayWin: observable,
      setReplayWin: action.bound,

      replayDialogWin: observable,
      setReplayDialogWin: action.bound,

      isReplayFinished: observable,
      setIsReplayFinished: action.bound,

      gamePaused: observable,
      setGamePaused: action.bound,

      gameReady: observable,
      setGameReady: action.bound,

      relaunchPending: observable,
      setRelaunchPending: action.bound,

      relaunchBlocked: observable,
      setRelaunchBlocked: action.bound,

      stakeAmount: observable,
      setStakeAmount: action.bound,

      roundInProgress: observable,
      setRoundInProgress: action.bound,

      soundEnabled: observable,
      setSoundEnabled: action.bound,

      canvasWidth: observable,
      setCanvasWidth: action.bound,

      rate: computed,
      gameId: computed,
      isDemo: computed,
      partnerId: computed,
      isTronix: computed,
      isSlotLine: computed,
      precision: computed,
      currencySymbol: computed,
      hasGameRedirection: computed,
      topWinnersEnabled: computed
    });
  }

  public get rate(): number {
    const { data } = this.launchData;
    if (!data) return 1;
    return data.rate;
  }

  public get precision(): number {
    const { data } = this.launchData;
    if (!data) return 2;
    return data.precision;
  }

  public get isTronix(): boolean {
    const { data } = this.launchData;
    if (!data) return false;
    return data.category === GameCategory.TronixZone;
  }

  public get isSlotLine(): boolean {
    const { data } = this.launchData;
    if (!data) return false;
    return data.category === GameCategory.Slot || data.category === GameCategory.TronixZone;
  }

  public get gameId(): number {
    const { data } = this.launchData;
    return data?.gameId || 0;
  }

  public get partnerId(): number {
    const { data } = this.launchData;
    return data?.partnerId || 0;
  }

  public get currencySymbol(): string {
    const { data } = this.launchData;
    return data?.currencySymbol ? decodeURIComponent(data.currencySymbol) : (data?.currency || '');
  }

  public get currencyCode(): string {
    const { data } = this.launchData;
    return data?.currency || '';
  }

  public get culture(): string {
    const { data } = this.launchData;
    return data?.culture || 'en';
  }

  public get isDemo(): boolean {
    const { data } = this.launchData;
    return data?.mode === GameMode.Fun;
  }

  /**
   * @description whether the partner (platform) supports redirecting the player into another game.
   * When false every promo tool drops its game-launching action: free spins are claim-only and the
   * player is told to open the game themselves, the pre-lobby offers demo mode only, and the
   * top-wins replay popups hide their play button. Partner-level, so `changeGame` keeps it across
   * the partial launch-info merge.
   */
  public get hasGameRedirection(): boolean {
    const { data } = this.launchData;
    return !!data?.hasGameRedirection;
  }

  public get topWinnersEnabled(): boolean {
    const { data } = this.launchData;
    return !!data?.topWinnersEnabled;
  }

  public setLaunchData(value: HttpResponse<LaunchData | null>): void {
    this.launchData = value;
  }

  public setGameErrorInfo(value: GameErrorInfo): void {
    this.gameErrorInfo = value;
  }

  public changeGameIdInLaunchData(gameId: number): void {
    if (this.launchData.data) {
      this.launchData.data.gameId = gameId;
    }
  }

  public setVirtualGames(value: HttpResponse<Array<VirtualGame>>): void {
    this.virtualGames = value;
  }
  
  public setTopWins(value: HttpResponse<Array<TopWin>>): void {
    this.topWins = value;
  }

  public setGameReplayUrl(value: HttpResponse<string>): void {
    this.gameReplayUrl = value;
  }

  public setReplayWin(value: TopWin | null): void {
    this.replayWin = value;
  }

  public setReplayDialogWin(value: TopWin | null): void {
    this.replayDialogWin = value;
  }

  public setIsReplayFinished(value: boolean): void {
    this.isReplayFinished = value;
  }

  public setGamePaused(value: boolean): void {
    this.gamePaused = value;
  }

  public setGameReady(value: boolean): void {
    this.gameReady = value;
  }

  public setStakeAmount(value: number): void {
    this.stakeAmount = value;
  }

  public setRoundInProgress(value: boolean): void {
    this.roundInProgress = value;
  }

  public setSoundEnabled(value: boolean): void {
    this.soundEnabled = value;
  }

  public setCanvasWidth(value: number): void {
    this.canvasWidth = value;
  }

  public handleSoundEnabledUpdate(soundEnabled: boolean): void {
    this.rootStore.freeDropStore.handleSoundEnabledUpdate(soundEnabled);
    this.setSoundEnabled(soundEnabled);
  }

  private checkIsGameUnavailable(data: Partial<LaunchData>): void {
    const isError = !!data?.isError;
    const isComingSoon = !!data?.isComingSoon;
    const isMaintanance = LaunchType.Maintenance === data?.launchType;
    if (isMaintanance || isError || isComingSoon) {
      this.rootStore.loaderStore.setGameLoaded(true);
      failLaunch(isMaintanance ? 'maintenance' : isError ? 'launch_error' : 'coming_soon', data?.errorMessage);
      finishLaunch('unavailable');
    }
  }

  public async getLaunchData(launchToken: string): Promise<void> {
    // One-time, 30 s token. A second redeem always 400s and costs the player a full relaunch.
    if (this.redeemedToken && this.redeemedToken === launchToken) return;
    this.setLaunchData(this.launchData.fetching());
    this.rootStore.userActivityStore.markLoad('launchInfoStart');
    const response = await gameController.getLaunchData(launchToken);
    // Cancelled because the page is going away: pagehide already recorded this launch as abandoned, and a relaunch
    // from here would only stamp the cooldown onto the page the player is navigating to.
    if (response?.aborted) return;
    const data = response?.result;
    
    this.rootStore.userActivityStore.markLoad('launchInfoEnd');

    if (data) {
      // Only on success: a failed first call may legitimately retry.
      this.redeemedToken = launchToken;
      markLaunchStep('info');
      setLaunchContext(data);

      if (data.isError) {
        const hasErrorCode = data.errorCode !== undefined && data.errorCode !== null;
        const message = data.errorMessage || '';

        let title = '';
        let displayErrorCode = '';
        let text = message ? `${message}_TEXT` : T.GENERAL_ERROR_TEXT;
        if (hasErrorCode) {
          title = message ? `${message}_TITLE` : T.GENERAL_ERROR_TITLE;
          displayErrorCode = `${ErrorPrefix.Platform}0000${data.errorCode}`;
        }

        this.setGameErrorInfo({
          title,
          text,
          displayErrorCode
        });
      }

      this.checkIsGameUnavailable(data);

      this.setLaunchData(this.launchData.fetched(data));

      // after setLaunchData: the store skips it when the launch already opens on the maintenance page
      if (data.maintenanceInfo) {
        this.rootStore.maintenanceStore.handleLaunchMaintenance(data.maintenanceInfo);
      }

      this.rootStore.sidebarMenuStore.announceActivePromos();

      const { partnerId, wheelInfo, tokenInfo, expiresIn, translations, gameId, category, currency, hasClaim, isAuthenticated, hasJackpot, awardedFreeBets, hasCashback } = data;
      if (awardedFreeBets) {
        this.rootStore.freeSpinStore.setFreeSpinInfo(awardedFreeBets);
      }

      this.rootStore.translationsStore.updateTranslations(translations);

      if (isAuthenticated) {
        this.rootStore.authStore.initAuthFlow(tokenInfo, expiresIn);
      }

      if (gameId !== CCEuro24GameId) {
        launcherSocket.startSocketLifecycle(partnerId, gameId, category, this.rootStore);
      }

      if (hasJackpot) {
        jackpotSocket.startSocketLifecycle(partnerId, gameId, currency, this.rootStore);
      }

      this.rootStore.cashbackStore.setShowCashback(hasCashback);

      if (data?.tournamentInfo !== null && data?.tournamentInfo?.id !== data?.tournamentId) {
        this.rootStore.tournamentStore.setTournamentInfoModalOpen(true);
      }

      if (hasClaim) {
        this.rootStore.tournamentStore.setTournamentHasClaimModalOpen(true);
      }

      if (wheelInfo) {
        await this.rootStore.boostedJackpotStore.loadBoostedJackpotResources();
        this.rootStore.boostedJackpotStore.setBoostedJackpotInfo(wheelInfo);
        if (wheelInfo?.hasWin) {
          this.rootStore.postMessageStore.sendPauseGame();
          this.rootStore.boostedJackpotStore.setHasBoostedJackpot(wheelInfo?.hasWin);
        }
      }
    } else {
      // Token miss (expired or never stored), or the request never reached the server: either way this page
      // instance is done and reloadGame re-runs the launch. 'info_network' keeps "never reached the server" out of
      // the token-miss series, as launch.negotiate.status = -1 does for the socket probe.
      if (response?.noResponse) {
        failLaunch('info_network', isOffline() ? 'offline' : 'no_response');
      } else {
        failLaunch('info', response?.errorMessage);
      }
      finishLaunch('error');
      this.relaunchOrBlock();
    }

  }

  public handleGameLoadFailed({ errorCode }: { errorCode: number }): void {
    failLaunch('game_load_failed', errorCode);
    finishLaunch('error');
    // Stop loading state
    this.rootStore.loaderStore.setGameLoaded(true);
    integration.current.hideGlobalLoader();
    const errorCodePrefix = ErrorPrefix[this.launchData.data?.category as keyof typeof ErrorPrefix] || '';

    this.setGameErrorInfo({
      title: T.OOPS_SOMETHING_WENT_WRONG_LAUNCHING_GAME,
      text: T.SOMETHING_WENT_WRONG,
      displayErrorCode: `${errorCodePrefix}0000${errorCode ?? ''}`,
    });
  }

  public async getGameLaunchInfo(params: GameLaunchInfoRequest, fromGameId: number, redirectSource: GameRedirectionSource): Promise<void> {
    this.rootStore.userActivityStore.beginGameSwitch();
    const gameInfo = await gameController.getGameLaunchInfo(params);
    if (!this.launchData.data) return;

    this.rootStore.userActivityStore.gameInitTime = Date.now();
    this.rootStore.userActivityStore.markLoad('launchInfoEnd');

    this.checkIsGameUnavailable(gameInfo);

    // START: send game redirection 
    const { gameId, category } = gameInfo;
    const { category: fromCategory } = this.launchData.data;
    launcherSocket.invokeGameRedirect({
      fromGameId,
      fromCategory,
      source: redirectSource,
      gameId: gameId as number,
      category: category as GameCategory
    });
    // END: send game redirection 

    const data: LaunchData = {
      ...this.launchData.data,
      ...gameInfo
    };

    const { hasJackpot, hasCashback, wheelInfo, partnerId, currency } = data;

    jackpotSocket.stop();
    if (hasJackpot) {
      jackpotSocket.startSocketLifecycle(partnerId, gameId as number, currency, this.rootStore);
    }

    this.rootStore.cashbackStore.setShowCashback(hasCashback);

    this.setLaunchData(this.launchData.fetched(data));
    this.setCanvasWidth(0);

    if (gameInfo.maintenanceInfo) {
      this.rootStore.maintenanceStore.handleLaunchMaintenance(gameInfo.maintenanceInfo);
    }

    // pg-wheel visibility is driven by `boostedJackpotStore.wheelInfo`, so it has to be re-seeded
    // from the new game's launch info — cleared when that game has no wheel, otherwise the same
    // seeding `getLaunchData` does. Kept after `setLaunchData` so loading the wheel resources
    // doesn't delay the new game's iframe.
    if (wheelInfo) {
      if (!this.rootStore.boostedJackpotStore.boostedJackpotResourcesLoaded) {
        await this.rootStore.boostedJackpotStore.loadBoostedJackpotResources();
      }
      this.rootStore.boostedJackpotStore.setBoostedJackpotInfo(wheelInfo);
    } else {
      this.rootStore.boostedJackpotStore.setWheelInfo(null);
    }
  }

  public async getVirtualGames(params: VirtualGamesRequest): Promise<void> {
    this.setVirtualGames(this.virtualGames.fetching());
    const virtualGames = await gameController.getVirtualGames(params);
    this.setVirtualGames(this.virtualGames.fetched(virtualGames));
  }

  public listenEvents(): void {
    launcherSocket.onBigWin(this.handleBigWin.bind(this));
  }

  public handleBigWin(data: BigWinEvent): void {
    if (data.userId === this.rootStore.gameStore.launchData?.data?.userId) return;
    if (this.bigWinFlushTimer === null) {
      this.pushBigWinNotification(data);
      this.bigWinFlushTimer = setInterval(this.flushBigWinBuffer.bind(this), THREE_MINUTES_MS);
      return;
    }
    this.bigWinBuffer.push(data);
  }

  private flushBigWinBuffer(): void {
    if (!this.bigWinBuffer.length) {
      // a quiet window — stop the timer so the next big win is again pushed immediately
      if (this.bigWinFlushTimer !== null) {
        clearInterval(this.bigWinFlushTimer);
        this.bigWinFlushTimer = null;
      }
      return;
    }
    const biggest = this.bigWinBuffer.reduce((max, win) => (win.odd > max.odd ? win : max));
    this.bigWinBuffer = [];
    this.pushBigWinNotification(biggest);
  }

  private pushBigWinNotification(data: BigWinEvent): void {
    const { maskedPlayerId, odd, gameName } = data;
    this.lastBigWin = data;
    const message = setTranslationValues(
      this.rootStore.translationsStore.translate(T.BIG_WIN_NOTIFICATION),
      { maskedPlayerId, odd, gameName },
    );
    this.rootStore.sidebarMenuStore.setHasTopWinsNotification(true);
    this.rootStore.sidebarNotificationStore.enqueue(
      message,
      SideMenuIcons.Win,
      NotificationPriority.High
    );
  }

  public openBigWinReplay(): void {
    if (!this.lastBigWin) return;
    const { betId, gameId, gameName, odd, maskedPlayerId, hasReplay } = this.lastBigWin;
    if (!hasReplay) return;
    this.openReplay({
      betId,
      gameId,
      gameName,
      odd,
      hasReplay,
      playerId: maskedPlayerId,
      betAmount: 0,
      winAmount: 0,
      currency: '',
      thumbnail: '',
      date: '',
    });
  }

  // Resolves with the rows shown, or null when the request failed (the list then shows empty).
  public async getTopWins(params: Omit<TopWinnersRequest, 'partnerId'>): Promise<Array<TopWin> | null> {
    // A demo player has no real bet history, so their personal list is always empty — resolve it
    // locally instead of asking the backend for wins that cannot exist.
    if (this.isDemo && params.isUserTop) {
      this.setTopWins(this.topWins.fetched([]));
      return [];
    }

    this.setTopWins(this.topWins.fetching());
    try {
      const topWins = await gameController.getTopWinners({ ...params, partnerId: this.partnerId });
      this.setTopWins(this.topWins.fetched(topWins));
      return topWins;
    } catch (error) {
      this.setTopWins(this.topWins.fetched([]));
      return null;
    }
  }

  // A replay started from a Top Winners card. rank = the card's place in the list, 1 = top.
  public trackTopWinsReplay(win: TopWin): void {
    const rank = this.topWins.data.findIndex(({ betId }) => betId === win.betId) + 1;
    if (!rank) return;
    this.rootStore.userActivityStore.sendTrackUserActivity({
      category: UserActivityCategory.PromoTools,
      type: UserActivityTypes.TopWinnersReplay,
      data: { gameId: win.gameId, rank }
    });
  }

  // gameId is passed explicitly because a replay may belong to a game other than the current one (e.g. all-games winners list)
  public async getGameReplayUrl(gameId: number, betId: number): Promise<void> {
    this.setGameReplayUrl(this.gameReplayUrl.fetching());
    const data = await gameController.getGameReplayUrl(gameId, { betId });
    this.setGameReplayUrl(this.gameReplayUrl.fetched(data.replayUrl));
  }

  public async openReplay(win: TopWin): Promise<void> {
    this.setReplayDialogWin(null);
    this.setIsReplayFinished(false);
    this.setReplayWin(win);
    this.muteGameForReplay();
    await this.getGameReplayUrl(win.gameId, win.betId);
  }

  public closeReplay(): void {
    this.setReplayWin(null);
    this.setIsReplayFinished(false);
    this.setGameReplayUrl(this.gameReplayUrl.fetching(true));
    this.unmuteGameAfterReplay();
  }

  // the replay frame plays its own audio; silence the game underneath while it is open
  private muteGameForReplay(): void {
    if (this.replayMutedGameSound || !this.soundEnabled) return;
    this.replayMutedGameSound = true;
    this.rootStore.postMessageStore.sendUpdateSettings({ sound: false });
  }

  // only undoes our own mute, so a player who had sound off stays muted
  private unmuteGameAfterReplay(): void {
    if (!this.replayMutedGameSound) return;
    this.replayMutedGameSound = false;
    this.rootStore.postMessageStore.sendUpdateSettings({ sound: true });
  }

  // "Play Now" from the replay scene/dialog: changeGame no-ops when the win belongs to the current game
  public playReplayGame(win: TopWin): void {
    this.setReplayDialogWin(null);
    this.closeReplay();
    this.rootStore.sidebarMenuStore.setIsSidebarOpen(false);
    integration.current.changeGame({ gameId: win.gameId, source: GameRedirectionSource.TopWins });
  }

  public setRelaunchPending(value: boolean): void {
    this.relaunchPending = value;
  }

  public setRelaunchBlocked(value: boolean): void {
    this.relaunchBlocked = value;
  }

  /**
   * For a launch that died before any launch data arrived: re-run it through the operator URL, or, when the cooldown
   * suppressed that, give the player the general ErrorPage with a Refresh button instead of a blank page.
   */
  public relaunchOrBlock(): void {
    if (this.reloadGame()) return;
    this.setGameErrorInfo({
      title: T.GENERAL_ERROR_TITLE,
      text: T.GENERAL_ERROR_TEXT,
      displayErrorCode: ''   // no platform code on this path; the render is guarded on truthiness
    });
    this.setRelaunchBlocked(true);
  }

  /** Milliseconds left on the relaunch cooldown, so a caller can re-enable its retry instead of dead-ending. */
  public relaunchCooldownRemaining(): number {
    try {
      const last = Number(sessionStorage.getItem(RELAUNCH_KEY) || 0);
      if (!last) return 0;
      return Math.max(0, RELAUNCH_COOLDOWN_MS - (Date.now() - last));
    } catch {
      return 0;
    }
  }

  /**
   * Every caller here navigates back to the operator URL, so a storm minute turned ~787 redirects into
   * 6,233 in ten seconds. Jitter spreads the fleet; the cooldown stops one player double-relaunching.
   * Either way `relaunchPending` goes true, so no caller is left with a button that silently does nothing.
   * Offline it waits for the network instead of navigating into a failure.
   * Returns false when the relaunch was suppressed.
   */
  public reloadGame(): boolean {
    // A relaunch is already scheduled, in flight or waiting for the network: this is the same intent again.
    if (this.relaunchTimer || this.stopWaitingForNetwork) return true;

    const remaining = this.relaunchCooldownRemaining();
    if (remaining > 0) {
      // Suppressed: hold the caller's button busy for what is left of the window, then let it try again.
      this.setRelaunchPending(true);
      setTimeout(() => this.setRelaunchPending(false), remaining);
      return false;
    }

    this.setRelaunchPending(true);
    if (isOffline()) {
      // Navigating now can only fail: WebKit keeps this page with the button stuck on Reloading, Chromium
      // and Gecko replace it with their own offline page. Relaunch once the network is back instead.
      this.waitForNetwork();
      return true;
    }
    this.scheduleRelaunch();
    return true;
  }

  private scheduleRelaunch(): void {
    try {
      // sessionStorage, not a field: the relaunch navigates away, so an in-memory counter resets on
      // the very page we are trying to rate-limit.
      sessionStorage.setItem(RELAUNCH_KEY, String(Date.now()));
    } catch {
      /* private mode: fall through, jitter alone still helps */
    }

    this.setRelaunchPending(true);
    // Thousands of pages get the same signal in the same second when the fleet loses its sockets.
    this.relaunchTimer = setTimeout(() => this.doReloadGame(), Math.random() * RELAUNCH_JITTER_MS);
  }

  private doReloadGame(): void {
    this.relaunchTimer = null;
    // The network dropped during the jitter; navigating would fail just the same.
    if (isOffline()) {
      this.waitForNetwork();
      return;
    }
    // A navigation that commits unloads this page, and this timer with it. One that fails or hangs does not
    // (WebKit keeps the page on a network error), and nothing else would clear relaunchPending. Armed first
    // so a URL that throws cannot strand the button either.
    this.relaunchTimer = setTimeout(() => this.handleRelaunchStalled(), RELAUNCH_STALL_MS);
    window.location.href = getRelaunchUrl();
  }

  private handleRelaunchStalled(): void {
    this.relaunchTimer = null;
    // Failed for want of a network: retry by itself once it is back rather than wait for another tap.
    if (isOffline()) {
      this.waitForNetwork();
      return;
    }
    this.setRelaunchPending(false);
  }

  /**
   * Keeps relaunchPending true, so the player can see the tap was taken. The poll backs up `online`, which a
   * page suspended in the background can miss (iOS, while the player is in Settings turning Wi-Fi back on).
   */
  private waitForNetwork(): void {
    if (this.stopWaitingForNetwork) return;

    const resume = (): void => {
      if (isOffline() || !this.stopWaitingForNetwork) return;
      this.stopWaitingForNetwork();
      this.scheduleRelaunch();
    };
    const poll = setInterval(resume, RELAUNCH_NETWORK_POLL_MS);
    window.addEventListener('online', resume);

    this.stopWaitingForNetwork = () => {
      clearInterval(poll);
      window.removeEventListener('online', resume);
      this.stopWaitingForNetwork = null;
    };
  }

}
